import { useContext, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import styles from './ImagePage.module.css';
import ImageTopBar from '../../components/ImageTopBar';
import TopBarBtn from '../../components/TopBarBtn';
import DeleteOutlineRoundedIcon from '@material-ui/icons/DeleteOutlineRounded';
import ShareOutlinedIcon from '@material-ui/icons/ShareOutlined';
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded';
import ArrowBackIosRoundedIcon from '@material-ui/icons/ArrowBackIosRounded';
import ProfilePic from '../../components/ProfilePic';
import SwitchImgBtn from '../../components/SwitchImgBtn';
import server from '../../apis/server';
import Spinner from '../../components/Spinner';

const ImagePage = () => {
  const history = useHistory();
  const { username, imageId } = useParams();
  const { isLoggedIn } = useContext(UserContext);

  const [hasLoaded, setHasLoaded] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [images, setImages] = useState(null);
  const [imageIndex, setImageIndex] = useState(-1);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setIsFetching(true);

        const images = await server.get(`/user/${username}/images`);

        if (isMounted) {
          setIsFetching(false);
          setImages(images.data);
        }
      } catch (err) {
        setIsFetching(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [history, username]);

  useEffect(() => {
    const switchImage = (direction) => {
      const id = nextImgId(direction);

      if (!id) return;

      setHasLoaded(false);

      history.push(`/user/${username}/image/${id}`);
    };

    const onKeyPress = (e) => {
      if (e.key === 'ArrowRight') {
        switchImage('next');
      } else if (e.key === 'ArrowLeft') {
        switchImage('previous');
      } else if (e.key === 'Escape') {
        history.push(`/user/${username}`);
      }
    };

    window.addEventListener('keydown', onKeyPress);

    return () => {
      window.removeEventListener('keydown', onKeyPress);
    };
  });

  const isVisible = () => {
    if (hasLoaded) {
      return {
        display: 'flex',
      };
    } else {
      return {
        display: 'none',
      };
    }
  };

  const switchBtnVisibility = (direction) => {
    if (!images) return 'hidden';

    if (direction === 'previous' && imageIndex === 0) {
      return 'hidden';
    } else if (direction === 'next' && imageIndex >= images.length - 1) {
      return 'hidden';
    }

    if (isFetching) {
      return 'hidden';
    } else {
      return 'visible';
    }
  };

  const nextImgId = (direction) => {
    if (!images) return;

    const index = images.findIndex((image) => image.id === imageId);

    if (imageIndex !== index) {
      setImageIndex(index);
    }

    if (direction === 'next') {
      if (index >= images.length - 1) return;
      return images[index + 1].id;
    } else {
      if (index <= 0) return;
      return images[index - 1].id;
    }
  };

  const formatDate = () => {
    const date = new Date(images[imageIndex].date);

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const onDeleteClick = async () => {
    if (window.confirm('Delete image?')) {
      try {
        setIsFetching(true);
        setHasLoaded(false);
        setIsDeleting(true);

        await server.delete(`/images/${imageId}`);

        setIsFetching(false);
        setHasLoaded(true);
        setIsDeleting(false);

        history.push(`/user/${username}`);
      } catch (err) {}
    }
  };

  return (
    <div className={styles.page}>
      <ImageTopBar>
        <Link to={`/user/${username}`}>
          <div className={styles.barLeft}>
            <ArrowBackIosRoundedIcon className={styles.backIcon} style={{ fontSize: '1.2rem' }} />
            <ProfilePic
              size="small"
              url={`${process.env.REACT_APP_SERVER_URL}/user/${username}/avatar`}
            />
          </div>
        </Link>
        <div className={styles.barButtons}>
          <TopBarBtn text="Share" icon={<ShareOutlinedIcon />} style={{ marginRight: '1rem' }} />
          <TopBarBtn text="Download" icon={<GetAppRoundedIcon />} style={{ marginRight: '1rem' }} />
          {isLoggedIn && isLoggedIn.username === username && (
            <TopBarBtn
              text="Delete"
              icon={<DeleteOutlineRoundedIcon />}
              style={{ color: '#FD8181' }}
              onClick={onDeleteClick}
            />
          )}
        </div>
      </ImageTopBar>
      <div className={styles.imgDisplay}>
        <Link
          to={`/user/${username}/image/${nextImgId('previous')}`}
          onClick={() => setHasLoaded(false)}
          style={{ visibility: switchBtnVisibility('previous') }}
        >
          <SwitchImgBtn />
        </Link>
        {!hasLoaded && isFetching && <Spinner />}
        {!hasLoaded && !isFetching && !isDeleting && images && images[imageIndex] && (
          <div
            className={styles.placeholder}
            style={{
              width: `${images[imageIndex].dimensions.width}px`,
              height: `${images[imageIndex].dimensions.height}px`,
              maxHeight: '80%',
              maxWidth: '80%',
            }}
          ></div>
        )}
        <img
          src={`${process.env.REACT_APP_SERVER_URL}/user/${username}/image/${imageId}`}
          alt=""
          style={isVisible()}
          className={styles.image}
          onLoad={() => setHasLoaded(true)}
          onError={() => history.push(`/user/${username}`)}
        />
        <Link
          to={`/user/${username}/image/${nextImgId('next')}`}
          onClick={() => setHasLoaded(false)}
          style={{ visibility: switchBtnVisibility('next') }}
        >
          <SwitchImgBtn rotation="180deg" display={switchBtnVisibility()} />
        </Link>
      </div>
      <div className={styles.info}>
        <span className={styles.user}>@{username}</span>
        {images && images[imageIndex] ? (
          <span className={styles.date}>{formatDate()}</span>
        ) : (
          <span className={styles.skeletonDate}>12.2.2112</span>
        )}
      </div>
    </div>
  );
};

export default ImagePage;
